import { useState } from 'react';

const transcriptLines = [
  { time: '14:32:11', speaker: 'ATC', text: 'Cessna 172 November 4 5 Tango, runway 28 cleared for takeoff, wind 270 at 8.', score: null },
  { time: '14:32:18', speaker: 'PILOT', text: 'Cleared for takeoff runway 28, Cessna 4 5 Tango.', score: 98, note: 'Excellent readback — complete and accurate.' },
  { time: '14:38:44', speaker: 'ATC', text: 'Cessna 4 5 Tango, enter left downwind runway 28, report abeam.', score: null },
  { time: '14:38:51', speaker: 'PILOT', text: 'Left downwind 28, wilco, 4 5 Tango.', score: 91, note: 'Good. Consider including "abeam" acknowledgment.' },
  { time: '14:41:05', speaker: 'PILOT', text: 'Cessna 4 5 Tango, abeam the numbers, touch and go.', score: 95, note: 'Proper position report with intentions.' },
  { time: '14:41:12', speaker: 'ATC', text: 'Cessna 4 5 Tango, number two, follow the Piper on final.', score: null },
  { time: '14:41:19', speaker: 'PILOT', text: 'Traffic in sight, number two, 4 5 Tango.', score: 99, note: 'Perfect traffic acknowledgment.' },
];

export default function FeatureRadioTranscription() {
  const [activeIdx, setActiveIdx] = useState<number | null>(2);

  return (
    <section id="radio-transcription" className="py-12 sm:py-24 bg-gradient-to-b from-[#0A1628] to-[#0D1B2E]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header - Stack on mobile */}
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-12 sm:mb-16">
          <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-[#00A3E0]/10 border border-[#00A3E0]/30 rounded-2xl flex-shrink-0">
            <i className="ri-radio-line text-2xl sm:text-3xl text-[#00A3E0]"></i>
          </div>
          <div>
            <div className="text-xs text-[#00A3E0] uppercase tracking-widest font-semibold mb-2">Capability 02</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">Radio Call Transcription</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl leading-relaxed">
              WingmanFix captures every ATC exchange and pilot transmission, transcribes it in real time, and scores your phraseology accuracy — so you can review exactly what was said and how well you said it.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 sm:gap-10">
          {/* Transcript Panel - Reduced max-height on mobile with scroll */}
          <div className="lg:col-span-3 bg-[#050A14] rounded-3xl border border-gray-700 overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-800 bg-[#0A1628]">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"></div>
                <span className="text-xs sm:text-sm font-semibold text-white uppercase tracking-wider">Live Transcript</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <i className="ri-bluetooth-line text-[#00A3E0]"></i>
                <span className="hidden sm:inline">Headset Connected</span>
              </div>
            </div>

            <div className="p-3 sm:p-4 space-y-2 max-h-[400px] sm:max-h-[480px] overflow-y-auto">
              {transcriptLines.map((line, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIdx(activeIdx === i ? null : i)}
                  className={`w-full text-left rounded-xl p-3 sm:p-4 border transition-all cursor-pointer ${
                    activeIdx === i
                      ? 'border-[#00A3E0]/60 bg-[#00A3E0]/10'
                      : 'border-gray-800 bg-[#0D1B2E] hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    <span className="text-xs text-gray-600 font-mono mt-0.5 flex-shrink-0">{line.time}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                          line.speaker === 'ATC'
                            ? 'bg-[#00A3E0]/20 text-[#00A3E0]'
                            : 'bg-[#00FFD1]/20 text-[#00FFD1]'
                        }`}>{line.speaker}</span>
                        {line.score !== null && (
                          <span className={`text-xs font-bold ml-auto ${line.score >= 95 ? 'text-[#B4FF39]' : line.score >= 88 ? 'text-[#00FFD1]' : 'text-yellow-400'}`}>
                            {line.score}/100
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-200 leading-relaxed">{line.text}</p>
                      {activeIdx === i && line.note && (
                        <div className="mt-3 flex items-start gap-2 bg-[#0A1628] rounded-lg p-3 border border-[#00FFD1]/20">
                          <i className="ri-lightbulb-line text-[#00FFD1] text-sm mt-0.5 flex-shrink-0"></i>
                          <p className="text-xs text-gray-300 leading-relaxed">{line.note}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Right: Stats + Info - Stack to single column on mobile */}
          <div className="lg:col-span-2 flex flex-col gap-4 sm:gap-6">
            <div className="bg-[#0D1B2E] rounded-3xl p-5 sm:p-8 border border-[#00A3E0]/20">
              <h3 className="text-sm sm:text-base font-semibold text-white uppercase tracking-wider mb-5 sm:mb-6">Session Summary</h3>
              <div className="space-y-4 sm:space-y-5">
                {[
                  { label: 'Phraseology Accuracy', value: 94, color: '#00A3E0' },
                  { label: 'Readback Completeness', value: 97, color: '#00FFD1' },
                  { label: 'Response Timing', value: 88, color: '#B4FF39' },
                  { label: 'Callsign Consistency', value: 100, color: '#00FFD1' },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="flex justify-between mb-1.5">
                      <span className="text-xs sm:text-sm text-gray-300">{s.label}</span>
                      <span className="text-xs sm:text-sm font-bold" style={{ color: s.color }}>{s.value}</span>
                    </div>
                    <div className="h-2 bg-[#1A2942] rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${s.value}%`, backgroundColor: s.color }}></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-gray-800">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Radio Score</p>
                <p className="text-4xl sm:text-5xl font-bold text-[#00A3E0]">94.7</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              {[
                { icon: 'ri-mic-line', title: 'Passive Capture', desc: 'Listens via Bluetooth headset — no button presses needed.' },
                { icon: 'ri-translate-2', title: 'Aviation Vocabulary', desc: 'Trained on FAA phraseology and ICAO standards.' },
                { icon: 'ri-file-text-line', title: 'Full Debrief Log', desc: 'Every call saved and searchable after the flight.' },
              ].map((item, i) => (
                <div key={i} className="bg-[#0F1F35] rounded-xl p-4 sm:p-5 border border-gray-700 hover:border-[#00A3E0]/40 transition-all flex items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-[#00A3E0]/10 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-lg sm:text-xl text-[#00A3E0]`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white mb-1">{item.title}</p>
                    <p className="text-xs text-gray-500 leading-snug">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}